import { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import {
  checkPermission,
  formatCurrency,
  formatNumber,
  formatString,
} from '../../utilities/helpers'
import { getAccountBalance, getPaymentListingsV4 } from '../../utilities/api'
import {
  dateTime,
  ORGANIZATION_PERMISSION_SLUGS,
} from '@blocktickets/shared-utils'

import {
  buildDailySettlementQueryParams,
  formatDailySettlementReportDateLabel,
} from '../DailySettlementWrapper/dailySettlementDateRange'

import {
  buildAccountingDashboardData,
  getMostRecentPayout,
  getYearToDatePayouts,
  sumPayoutAmounts,
} from './accountingDashboardData'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import ProgressBar from 'react-bootstrap/ProgressBar'
import Row from 'react-bootstrap/Row'
import Table from 'react-bootstrap/Table'

import { DateInputWrapper } from '../DateInputWrapper'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

import './accountingDashboardWrapper.scss'

const FEE_BAR_VARIANTS = {
  custom: 'primary',
  processing: 'info',
  facility: 'warning',
  tax: 'secondary',
}

const DEFAULT_DATE = () => moment().subtract(1, 'day').startOf('day').toDate()

function KpiCard({ title, value, subtitle, valueClassName = '' }) {
  return (
    <Card body className="card--xs card-with-border accounting-dashboard-kpi">
      <Card.Title as="h6" className="text-muted">
        {title}
      </Card.Title>
      <div className={`accounting-dashboard-kpi-value ${valueClassName}`}>
        {value}
      </div>
      {subtitle ? (
        <p className="accounting-dashboard-kpi-sub text-muted">{subtitle}</p>
      ) : null}
    </Card>
  )
}

function SectionHead({ title, count }) {
  return (
    <div className="accounting-dashboard-section-head">
      <h2>{title}</h2>
      {count != null ? (
        <span className="accounting-dashboard-section-count">{count}</span>
      ) : null}
    </div>
  )
}

export default function AccountingDashboardWrapper({ organization }) {
  const { isLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_SETTLEMENT_REPORT
    )
  const canViewPayouts =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_BALANCE_PAYOUTS
    )

  const [selectedDate, setSelectedDate] = useState(DEFAULT_DATE)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [payouts, setPayouts] = useState([])
  const [bankAccount, setBankAccount] = useState(null)

  const year = moment(selectedDate || undefined).year()

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    hideLoading()
  }, [orgPermissionsReady, hasPermission, hideLoading])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission || !canViewPayouts) {
      setPayouts([])
      setBankAccount(null)
      return
    }

    const accountId = organization?.connected_account_id
    if (!accountId) {
      setPayouts([])
      setBankAccount(null)
      return
    }

    let cancelled = false
    getAccountBalance(accountId)
      .then((res) => {
        if (cancelled) return
        setPayouts(res?.data?.payouts?.data || [])
        setBankAccount(res?.data?.linkedBankAccount || null)
      })
      .catch((err) => {
        console.error('Failed to load payouts for accounting dashboard:', err)
        if (!cancelled) {
          setPayouts([])
          setBankAccount(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    organization?.connected_account_id,
    orgPermissionsReady,
    hasPermission,
    canViewPayouts,
  ])

  useEffect(() => {
    const fetchData = async () => {
      if (!orgPermissionsReady || !hasPermission) return
      if (!organization?.uuid || !selectedDate) return

      setIsLoadingData(true)
      try {
        const tz = dateTime.getBrowserTimezone('UTC')
        const { fromUtcIso, toUtcIso, options } =
          buildDailySettlementQueryParams(selectedDate, selectedDate, tz)

        const res = await getPaymentListingsV4(
          organization.uuid,
          fromUtcIso,
          toUtcIso,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          options
        )

        setDashboardData(buildAccountingDashboardData(res?.data || []))
      } catch (error) {
        console.error('Error fetching accounting dashboard data:', error)
        setDashboardData(null)
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchData()
  }, [selectedDate, organization?.uuid, orgPermissionsReady, hasPermission])

  const ytdPayouts = useMemo(
    () => getYearToDatePayouts(payouts, year),
    [payouts, year]
  )
  const ytdPayoutTotal = useMemo(
    () => sumPayoutAmounts(ytdPayouts),
    [ytdPayouts]
  )
  const mostRecentPayout = useMemo(
    () => getMostRecentPayout(payouts),
    [payouts]
  )

  const shiftDate = (days) => {
    setSelectedDate((current) =>
      moment(current || DEFAULT_DATE())
        .add(days, 'days')
        .startOf('day')
        .toDate()
    )
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  const kpis = dashboardData?.kpis
  const dateLabel = formatDailySettlementReportDateLabel(
    selectedDate,
    selectedDate
  )
  const bankName = bankAccount?.data?.[0]?.bank_name

  return (
    <section
      id="accounting-dashboard-wrapper"
      className="spacer-sm"
      data-testid="accounting-dashboard-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Accounting Dashboard</h1>
        </div>
        <p className="section-header-desc">
          Daily close view of sales, fees, and payouts for{' '}
          {organization?.name || 'your organization'}.
        </p>
      </header>

      <div className="accounting-dashboard-actions">
        <div className="accounting-dashboard-date-nav">
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => shiftDate(-1)}
            disabled={isLoadingData}
            aria-label="Previous day"
          >
            ‹
          </Button>
          <Form.Group controlId="accounting-dashboard-date" className="mb-0">
            <Form.Label className="visually-hidden">Date</Form.Label>
            <div style={{ width: '220px' }}>
              <DateInputWrapper
                id="accounting-dashboard-date"
                startDate={selectedDate}
                selectedDate={selectedDate}
                setDate={setSelectedDate}
                showMinDate={false}
                isReadOnly={isLoadingData}
                size="sm"
                styles="form-control-height"
              />
            </div>
          </Form.Group>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => shiftDate(1)}
            disabled={isLoadingData}
            aria-label="Next day"
          >
            ›
          </Button>
        </div>
        <Button
          as={Link}
          to="/daily-settlement"
          variant="outline-light"
          size="sm"
        >
          Open Daily Settlement
        </Button>
      </div>

      {isLoadingData ? (
        <LoadingContainer />
      ) : !dashboardData ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>
              Unable to load accounting data for {dateLabel || 'this date'}.
            </p>
            <p className="subtitle subtitle--dark">
              Please try another date or refresh the page.
            </p>
          </EmptyContainer>
        </Card>
      ) : (
        <>
          <div className="accounting-dashboard-kpi-row">
            <KpiCard
              title="Gross Sales"
              value={formatCurrency(kpis.grossSales)}
              subtitle={`${formatNumber(kpis.completeOrderCount)} orders`}
            />
            <KpiCard
              title="Fees Collected"
              value={formatCurrency(kpis.feesCollected)}
              subtitle={`${kpis.feePctOfGross.toFixed(1)}% of gross`}
            />
            <KpiCard
              title="Refunds"
              value={`−${formatCurrency(kpis.refunds)}`}
              valueClassName="is-danger"
              subtitle={`${formatNumber(kpis.refundCount)} refunds`}
            />
            <KpiCard
              title="Net Sales"
              value={formatCurrency(kpis.netSales)}
              subtitle="after fees & refunds"
            />
            <KpiCard
              title={`Payouts Sent (${year})`}
              value={canViewPayouts ? formatCurrency(ytdPayoutTotal) : '—'}
              valueClassName={canViewPayouts ? 'is-success' : ''}
              subtitle={
                canViewPayouts
                  ? `${formatNumber(ytdPayouts.length)} payouts YTD`
                  : 'Payouts permission required'
              }
            />
            <KpiCard
              title="Reconciliation"
              value={`${kpis.reconciliationPct.toFixed(1)}% matched`}
              subtitle={
                kpis.needsReviewCount > 0
                  ? `${formatNumber(kpis.needsReviewCount)} rows need review`
                  : 'All rows reconcilable'
              }
            />
          </div>

          <Row className="g-3">
            <Col lg={6}>
              <SectionHead
                title="Most Recent Payout"
                count={
                  canViewPayouts && mostRecentPayout
                    ? formatCurrency(mostRecentPayout.amount / 100)
                    : null
                }
              />
              <Card body className="card--md h-100">
                {!canViewPayouts ? (
                  <div className="accounting-dashboard-empty">
                    <p className="mb-0 text-muted">
                      You need payouts permission to view payout details.
                    </p>
                  </div>
                ) : !mostRecentPayout ? (
                  <div className="accounting-dashboard-empty">
                    <p className="mb-0 text-muted">No payouts found.</p>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div className="fw-semibold mb-1">
                        {bankName || 'Connected bank account'}
                      </div>
                      <div className="accounting-dashboard-payout-meta">
                        Stripe · {mostRecentPayout.id}
                      </div>
                      <div className="accounting-dashboard-payout-meta">
                        Created{' '}
                        {moment
                          .unix(mostRecentPayout.created)
                          .format('MMM D, YYYY h:mm A')}
                      </div>
                      <div className="accounting-dashboard-payout-meta">
                        Est. arrival{' '}
                        {moment
                          .unix(mostRecentPayout.arrival_date)
                          .add(1, 'day')
                          .format('MMM D, YYYY')}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="fw-semibold mb-2">
                        {formatCurrency(mostRecentPayout.amount / 100)}
                      </div>
                      <Badge
                        bg="none"
                        className={`badge--${mostRecentPayout.status}`}
                      >
                        {formatString(mostRecentPayout.status)}
                      </Badge>
                      <div className="mt-3">
                        <Button
                          as={Link}
                          to={`/payouts-report/payouts/${mostRecentPayout.id}`}
                          variant="outline-light"
                          size="sm"
                        >
                          View payout
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </Col>
            <Col lg={6}>
              <SectionHead
                title="Exceptions & Flags"
                count={`${formatNumber(kpis.needsReviewCount)} open`}
              />
              <Card body className="card--md h-100">
                {kpis.needsReviewCount > 0 ? (
                  <div className="d-flex justify-content-between gap-3">
                    <div>
                      <div className="fw-semibold">
                        Payment plan orders — unreconcilable at row level
                      </div>
                      <div className="accounting-dashboard-payout-meta">
                        Installment timing can split gross from net per row.
                        Reconcile at plan level instead.
                      </div>
                    </div>
                    <div className="fw-semibold">
                      {formatNumber(kpis.needsReviewCount)}
                    </div>
                  </div>
                ) : (
                  <div className="accounting-dashboard-empty">
                    <p className="mb-0 text-muted">
                      No reconciliation exceptions for this date.
                    </p>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          <SectionHead
            title="Transactions by Payment Type"
            count={`${formatNumber(kpis.completeOrderCount)} orders · ${dateLabel}`}
          />
          <Card body className="card--md">
            {(dashboardData.byPaymentMethod?.length || 0) === 0 ? (
              <EmptyContainer style="center lg report">
                <p>No transactions for {dateLabel}.</p>
                <p className="subtitle subtitle--dark">
                  Try another date or review Daily Settlement for more detail.
                </p>
              </EmptyContainer>
            ) : (
              <div className="table-container border-bottom-0">
                <Table>
                  <thead>
                    <tr className="md">
                      <th className="uppercase text-body">Payment Type</th>
                      <th className="uppercase text-body text-left">Orders</th>
                      <th className="uppercase text-body text-left">Tickets</th>
                      <th className="uppercase text-body text-left">Gross</th>
                      <th className="uppercase text-body text-left">
                        Service Fees
                      </th>
                      <th className="uppercase text-body text-left">
                        Facility Fee
                      </th>
                      <th className="uppercase text-body text-left">
                        Custom Fees
                      </th>
                      <th className="uppercase text-body text-left">
                        Processing Fees
                      </th>
                      <th className="uppercase text-body text-left">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.byPaymentMethod.map((row) => (
                      <tr key={row.paymentMethod} className="md-x">
                        <td className="whitespace-nowrap">
                          {row.paymentMethod
                            ? formatString(row.paymentMethod)
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatNumber(row.orderCount)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatNumber(row.ticketsSold)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(row.grossSales)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(row.serviceFees)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(row.facilityFees ?? 0)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(row.additionalFees ?? 0)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(row.processingFees)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(row.netSales)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>

          <SectionHead
            title="Event Sales Summary"
            count={`${formatNumber(dashboardData.byEvent?.length || 0)} events`}
          />
          <Card body className="card--md">
            {(dashboardData.byEvent?.length || 0) === 0 ? (
              <EmptyContainer style="center lg report">
                <p>No event sales for {dateLabel}.</p>
              </EmptyContainer>
            ) : (
              <div className="table-container border-bottom-0">
                <Table>
                  <thead>
                    <tr className="md">
                      <th className="uppercase text-body">Event</th>
                      <th className="uppercase text-body text-left">Tickets</th>
                      <th className="uppercase text-body text-left">Gross</th>
                      <th className="uppercase text-body text-left">Fees</th>
                      <th className="uppercase text-body text-left">
                        Facility Fee
                      </th>
                      <th className="uppercase text-body text-left">Net</th>
                      <th className="uppercase text-body text-left">
                        Event Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.byEvent.map((event) => (
                      <tr key={event.eventKey} className="md-x">
                        <td>
                          <div className="fw-semibold">{event.eventName}</div>
                          {event.venueName ? (
                            <div className="accounting-dashboard-payout-meta">
                              {event.venueName}
                            </div>
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatNumber(event.tickets)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(event.gross)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(event.fees)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(event.facilityFees ?? 0)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {formatCurrency(event.net)}
                        </td>
                        <td className="whitespace-nowrap text-left">
                          {event.eventDate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>

          <SectionHead title="Fee & Tax Breakdown" count={dateLabel} />
          <Row className="g-3">
            <Col lg={6}>
              <Card body className="card--md h-100">
                <Card.Title as="h6" className="text-muted mb-3">
                  By fee type
                </Card.Title>
                {dashboardData.feeBreakdown.every((row) => row.amount <= 0) ? (
                  <p className="mb-0 text-muted">No fees for this date.</p>
                ) : (
                  dashboardData.feeBreakdown.map((row) => (
                    <div key={row.key} className="accounting-dashboard-fee-row">
                      <div className="accounting-dashboard-fee-label">
                        <span>{row.label}</span>
                        <span className="fw-semibold">
                          {formatCurrency(row.amount)}
                        </span>
                      </div>
                      <ProgressBar
                        now={row.pct}
                        variant={FEE_BAR_VARIANTS[row.key] || 'primary'}
                      />
                    </div>
                  ))
                )}
                <p className="accounting-dashboard-payout-meta mt-3 mb-0">
                  Processing fees use estimated processing fee when available,
                  otherwise the order processing fee.
                </p>
              </Card>
            </Col>
            <Col lg={6}>
              <Card body className="card--md h-100">
                <Card.Title as="h6" className="text-muted mb-3">
                  By payment type
                </Card.Title>
                {(dashboardData.feesByPaymentMethod?.length || 0) === 0 ? (
                  <p className="mb-0 text-muted">
                    No payment-type fee totals for this date.
                  </p>
                ) : (
                  (() => {
                    const maxFees = Math.max(
                      ...dashboardData.feesByPaymentMethod.map((row) =>
                        Number(row.fees || 0)
                      ),
                      1
                    )
                    return dashboardData.feesByPaymentMethod.map((row) => (
                      <div
                        key={row.paymentMethod}
                        className="accounting-dashboard-fee-row"
                      >
                        <div className="accounting-dashboard-fee-label">
                          <span>
                            {row.paymentMethod
                              ? formatString(row.paymentMethod)
                              : '—'}
                          </span>
                          <span className="fw-semibold">
                            {formatCurrency(row.fees)}
                          </span>
                        </div>
                        <ProgressBar
                          now={(Number(row.fees || 0) / maxFees) * 100}
                          variant="primary"
                        />
                      </div>
                    ))
                  })()
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </section>
  )
}
